import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { Box, Paper, Typography, IconButton, Tooltip as MuiTooltip, useTheme } from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import { saveAs } from 'file-saver';

import SystemGeneratedMarkdown from "./SystemGeneratedMarkdown";

const D3HeatMapShell = ({ config }) => {
    const chartRef = useRef();
    const containerRef = useRef();
    const theme = useTheme();
    const [fadeIn, setFadeIn] = useState(false);

    useEffect(() => {
        if (!config) return;

        const { chart, data, axes, colors, tooltip, legend, animations } = config;
        const { margin, responsive } = chart;

        const updateChart = () => {
            const width = responsive ? containerRef.current.clientWidth : (chart.width || 900);
            const height = chart.height || 500;

            // Extract categories dynamically (excluding the x-axis field)
            const categories = Object.keys(data.dataset[0]).filter(key => key !== axes.x.field);

            // Select the SVG container and clear previous content
            const svg = d3.select(chartRef.current)
                .attr("width", width)
                .attr("height", height)
                .style("background", chart?.background?.color || "#fff");

            svg.selectAll("*").remove(); // Clear previous chart

            // Adjust chart area position based on legend position
            let chartAreaTransformY = margin.top;
            if (legend.enabled && legend.positionY === "bottom") {
                chartAreaTransformY = margin.top / 2; // Move chart area closer to the top
            }
            if (legend.enabled && legend.positionY === "top") {
                chartAreaTransformY = margin.top + 20; // Move chart area closer to the top
            }

            const chartArea = svg.append("g")
                .attr("transform", `translate(${margin.left},${chartAreaTransformY})`);

            const xScale = d3.scaleBand()
                .domain(data.dataset.map(d => d[axes.x.field]))
                .range([0, width - margin.left - margin.right])
                .padding(0.05);

            const yScale = d3.scaleBand()
                .domain(categories)
                .range([height - margin.top - margin.bottom, 0])
                .padding(0.05);

            // Ensure colors.domain is defined and iterable
            const colorDomain = colors.palette.domain || [0, 100];
            const colorGradient = colors.palette.gradient || ["#f7fbff", "#08519c"];

            const colorScale = d3.scaleLinear()
                .domain(colorDomain)
                .range(colorGradient);

            // Draw Axes
            const xAxis = d3.axisBottom(xScale)
                .tickValues(xScale.domain().filter((d, i) => !(i % axes.x.labelSettings.interval)))
                .tickFormat(d3.format("d")); // Format as integer

            const yAxis = d3.axisLeft(yScale)
                .tickValues(yScale.domain().filter((d, i) => !(i % axes.y.labelSettings.interval)));

            chartArea.append("g")
                .attr("transform", `translate(0, ${height - margin.top - margin.bottom})`)
                .call(xAxis)
                .selectAll("text")
                .style("font-size", axes.x.labelSettings.font.size)
                .style("font-weight", axes.x.labelSettings.font.weight)
                .style("fill", axes.x.labelSettings.font.color);

            chartArea.append("g")
                .call(yAxis)
                .selectAll("text")
                .style("font-size", axes.y.labelSettings.font.size)
                .style("font-weight", axes.y.labelSettings.font.weight)
                .style("fill", axes.y.labelSettings.font.color);

            // Draw Heat Map
            chartArea.selectAll()
                .data(data.dataset.flatMap(d => categories.map(key => ({ day: d[axes.x.field], department: key, value: d[key] })))) // Updated line
                .enter()
                .append("rect")
                .attr("x", d => xScale(d.day))
                .attr("y", d => yScale(d.department))
                .attr("width", xScale.bandwidth())
                .attr("height", yScale.bandwidth())
                .attr("fill", d => colorScale(d.value))


            // Tooltip
            const tooltipDiv = d3.select(containerRef.current).append("div")
                .attr("class", "d3-tooltip")
                .style("position", "absolute")
                .style("background", tooltip.background || "#222")
                .style("color", tooltip.textColor || "#fff")
                .style("padding", "8px")
                .style("border-radius", "5px")
                .style("font-size", "12px")
                .style("pointer-events", "none")
                .style("opacity", 0);

            chartArea.selectAll("rect")
                .on("mouseover", function (event, d) {
                    tooltipDiv.transition().duration(200).style("opacity", tooltip.opacity || 0.9);
                    tooltipDiv
                        .style("left", `${event.pageX + 10}px`)
                        .style("top", `${event.pageY - 30}px`)
                        .html(`<strong>${d.department}:</strong> ${d.value}`);
                    d3.select(this).attr("fill-opacity", 0.5); // Change fill opacity
                })
                .on("mousemove", function (event) {
                    tooltipDiv
                        .style("left", `${event.pageX + 10}px`)
                        .style("top", `${event.pageY - 30}px`);
                })
                .on("mouseout", function (event, d) {
                    tooltipDiv.transition().duration(200).style("opacity", 0);
                    d3.select(this).transition().duration(200).attr("fill-opacity", 1); // Transition fill opacity back to 1
                });

            // Draw Legend (if enabled)
            if (legend.enabled) {
                const legendGroup = svg.append("g")
                    .attr("class", "legend");

                const legendItems = legendGroup.selectAll(".legend-item")
                    .data(colorGradient)
                    .enter()
                    .append("g")
                    .attr("class", "legend-item");

                legendItems.append("rect")
                    .attr("x", 0)
                    .attr("y", 0)
                    .attr("width", 18)
                    .attr("height", 18)
                    .attr("fill", d => d);

                legendItems.append("text")
                    .attr("x", 24)
                    .attr("y", 9)
                    .attr("dy", "0.35em")
                    .text((d, i) => `${colorDomain[i]}`)
                    .style("font-size", "12px")
                    .style("fill", "#000");

                // Set legend orientation
                if (legend.orientation === "horizontal") {
                    legendItems.attr("transform", (d, i) => `translate(${i * 100}, 0)`);
                } else if (legend.orientation === "vertical") {
                    legendItems.attr("transform", (d, i) => `translate(0, ${i * 20})`);
                }


                // 🔥 NEW: Dynamically Calculate Legend Width After Rendering
                const legendBBox = legendGroup.node().getBBox(); // Get actual bounding box of legend
                const legendWidth = legendBBox.width; // Use actual rendered width
                const legendHeight = legendBBox.height;

                let legendX = margin.left;
                let legendY = margin.top;

                // Adjust X-position
                if (legend.positionX === "middle") {
                    legendX = (width - legendWidth) / 2; // Centered
                } else if (legend.positionX === "right") {
                    legendX = width - margin.right - legendWidth + 50; // Align right
                } else if (legend.positionX === "left") {
                    legendX = margin.left + 10;
                }

                // Adjust Y-position
                if (legend.positionY === "top") {
                    legendY = 0;
                } else if (legend.positionY === "middle") {
                    legendY = (height - margin.top - margin.bottom) / 2 + margin.top - legendHeight / 2;
                } else if (legend.positionY === "bottom") {
                    legendY = height - margin.bottom + 40;
                }

                // Apply correct transformation to legend group
                legendGroup.attr("transform", `translate(${legendX}, ${legendY})`);
            }
        };

        // Initial chart rendering
        updateChart();

        // Resize observer to update chart on container resize
        const resizeObserver = new ResizeObserver(() => {
            if (responsive) {
                updateChart();
            }
        });

        resizeObserver.observe(containerRef.current);

        // Cleanup on component unmount
        return () => {
            resizeObserver.disconnect();
        };
    }, [config]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setFadeIn(true);
        }, 1000);

        return () => clearTimeout(timer);

    }, []);

    // Download Chart Image
    const downloadChartImage = () => {
        const svgElement = chartRef.current;
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svgElement);
        const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
        saveAs(blob, "heatmap.svg");
    };

    return (
        <Paper ref={containerRef} elevation={3} sx={{
            backgroundColor: config.chart.background?.color || "#fff"
        }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{
                padding: 2,
                backgroundColor: config.chart.background?.color || "#fff"
            }}>
                <Typography variant="h6">{config.chart.title}</Typography>
                <MuiTooltip title="Save Image">
                    <IconButton color="primary" onClick={downloadChartImage}>
                        <SaveIcon />
                    </IconButton>
                </MuiTooltip>
            </Box>
            <Box sx={{
                position: "relative",
                width: "100%",
                opacity: fadeIn ? 1 : 0,
                transition: "opacity 0.5s ease-in"
            }}>
                <svg ref={chartRef}></svg>
            </Box>
            {config.markdown && (
                <SystemGeneratedMarkdown config={config.markdown} />
            )}
        </Paper>
    );
};

export default D3HeatMapShell;